import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ArrowRight, Package, FileText } from 'lucide-react';
import styles from './SearchBar.module.css';

const SearchBar = ({ 
  projects = [], 
  onFileOpen = null, 
  onProjectSelect = null,
  placeholder = "Search projects and files...",
  size = "large", // "small" for topbar, "large" for landing
  className = "",
  showButton = true
}) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);

  // Get all searchable items from projects
  const getSearchableItems = () => {
    let searchableItems = [];
    
    if (!projects || !Array.isArray(projects)) return searchableItems;
    
    // Add projects
    projects.forEach(project => {
      searchableItems.push({
        type: 'project',
        id: project.id,
        name: project.name,
        title: project.name,
        subtitle: 'Project',
        project: project
      });

      // Add files from phases
      if (project.phases && Array.isArray(project.phases)) {
        project.phases.forEach(phase => {
          if (phase.files && Array.isArray(phase.files)) {
            phase.files.forEach(file => {
              searchableItems.push({
                type: 'file',
                id: file.id,
                name: file.name,
                title: file.name,
                subtitle: `${project.name} / ${phase.name}`,
                project: project,
                phase: phase,
                file: file
              });
            });
          }
        });
      }
    });
    
    return searchableItems;
  };

  // Handle search input changes
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    if (!query.trim()) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    // Filter searchable items
    const allItems = getSearchableItems();
    const filtered = allItems.filter(item =>
      item.name.toLowerCase().includes(query.toLowerCase()) ||
      item.subtitle.toLowerCase().includes(query.toLowerCase())
    );
    
    setSearchResults(filtered.slice(0, 8)); // Limit to 8 results
    setShowDropdown(filtered.length > 0);
  };

  // Handle search submission
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim() && searchResults.length > 0) {
      handleSearchResultClick(searchResults[0]);
    }
  };

  // Handle search result selection
  const handleSearchResultClick = (result) => {
    if (result.type === 'project' && onProjectSelect) {
      onProjectSelect(result.project);
    } else if (result.type === 'file' && onFileOpen) {
      // Call onFileOpen for both TopBar and Landing page usage
        onFileOpen(result.file, result.project, result.phase);
    }
    setSearchQuery("");
    setShowDropdown(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.search-bar-container')) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Size-specific styling
  const getSizeClasses = () => {
    if (size === 'small') {
      return {
        container: `${styles.searchContainerSmall} search-bar-container`,
        input: styles.searchInputSmall,
        icon: styles.searchIconSmall,
        button: styles.searchButtonSmall,
        buttonIcon: styles.searchButtonIconSmall,
        dropdown: styles.searchDropdownSmall
      };
    } else {
      return {
        container: `${styles.searchContainerLarge} search-bar-container`,
        input: styles.searchInput,
        icon: styles.searchIcon,
        button: styles.searchButton,
        buttonIcon: styles.searchButtonIcon,
        dropdown: styles.searchDropdown
      };
    }
  };

  const sizeStyles = getSizeClasses();

  return (
    <div className={`${styles.searchContainer} ${sizeStyles.container} ${className}`}>
      <form onSubmit={handleSearch} className={styles.searchForm}>
        <div className={styles.searchInputContainer}>
          <Search className={sizeStyles.icon} />
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder={placeholder}
            className={sizeStyles.input}
          />
          {showButton && (
            <button
              type="submit"
              className={sizeStyles.button}
            >
              <ArrowRight className={sizeStyles.buttonIcon} />
            </button>
          )}
        </div>
      </form>

      {/* Search dropdown */}
      {showDropdown && searchResults.length > 0 && (
        <div className={sizeStyles.dropdown}>
          {searchResults.map((result, index) => (
            <div
              key={`${result.type}-${result.id}`}
              className={styles.searchResult}
              onClick={() => handleSearchResultClick(result)}
            >
              <div className={result.type === 'project' ? styles.resultIcon : styles.resultIconFile}>
                {result.type === 'project' ? (
                  <Package className="h-4 w-4" />
                ) : (
                  <FileText className="h-4 w-4" />
                )}
              </div>
              <div className={styles.resultContent}>
                <p className={styles.resultTitle}>
                  {result.title}
                </p>
                <p className={styles.resultSubtitle}>
                  {result.subtitle}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchBar; 